import { useCallback, useEffect } from "react";
import { isPressed } from "../../Utils";
import { isKeyboardEvent } from "../../Utils/TypeGuards";
//we might want the onDown to pass the keyboard event through for some cases (but I can't think of any)
//I think it's clearer for now to just have an empty params list, so the user doesn't think they have to check for their key or w/e
//but we should add this if we find we need it.
function handleHotkey(e, onDown, hotkey, blur, ...modifiers) {
    if (!isKeyboardEvent(e)) {
        //Event listener on keydown will always receive KeyboardEvents
        //but the event type is just a string, and addEventListener takes an (Event) => void, not a (KeyboardEvent) => void
        //so we need to check this anyway to make the compiler happy (and blow up early if we've passed the wrong stuff into addEventListener or our runtime environment is *really* busted)
        throw new Error("Expected keyboard event from keydown event listener, got something else instead. This is probably developer error.");
    }
    if (isPressed(e, hotkey, ...modifiers)) {
        e.preventDefault(); //this prevents this keyboard event from triggering default browser behaviour, but doesn't stop it from bubbling to other event handlers on the page.
        if (blur) {
            //see doc comment on useHotkey/useGlobalHotkey
            // This was written prior to adding @typescript-eslint/consistent-type-assertions, please refactor when possible.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const activeElement = document.activeElement;
            if (activeElement.blur && activeElement.focus) {
                activeElement.blur();
                activeElement.focus();
            }
        }
        onDown(e);
    }
}
/**
 * Hotkey that will have its input listener wrapped around the passed in element.
 * Generally prefer this over useGlobalHotkey - most hotkeys should be scoped to only be active when focus in on a specific part of the UI
 * @param onDown function to be called when the hotkey is pressed
 * @param element element to wrap the keyboard event listener around
 * @param key the key to listen for
 * @param blur whether to trigger a blur and refocus on the active element (to trigger onChanged behaviour - caller will still likely also need a ref set in the onChanged in order to get up to date data inside onDown)
 * @param modifiers any modifiers to listen for
 */
export function useHotkey(onDown, element, key, blur, ...modifiers) {
    const onKeyDown = useCallback((e) => handleHotkey(e, onDown, key, blur, ...modifiers), [blur, key, modifiers, onDown]);
    useEffect(() => {
        const currentElement = element?.current;
        if (currentElement != null) {
            currentElement.addEventListener("keydown", onKeyDown);
            return () => {
                currentElement.removeEventListener("keydown", onKeyDown);
            };
        }
        else {
            return () => {
                return undefined; //nothing to do in the cleanup because we didn't register anything
            };
        }
    }, [element, onKeyDown]);
}
/**
 * Hotkey that will have its input listener registered on the whole document.
 * Prefer useHotkey over this unless you need the hotkey to apply no matter where on the page the user has focus.
 * @param onDown function to be called when the hotkey is pressed
 * @param key the key to listen for
 * @param blur whether to trigger a blur and refocus on the active element (to trigger onChanged behaviour - caller will still likely also need a ref set in the onChanged in order to get up to date data inside onDown)
 * @param modifiers any modifiers to listen for
 */
export function useGlobalHotkey(onDown, key, blur, ...modifiers) {
    const onKeyDown = useCallback((e) => handleHotkey(e, onDown, key, blur, ...modifiers), [blur, key, modifiers, onDown]);
    useEffect(() => {
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [onKeyDown]);
}
